// src/hooks/useSidebarPref.ts
//
// Shared state for the collapsible / pinnable section sidebar used by the
// Analytics, Financials, and Production pages (via <SideNav>).
//
// Two user-controlled modes, persisted per-browser in localStorage:
//   - PINNED  (default): the sidebar stays expanded (full labels).
//   - UNPINNED: the sidebar collapses to a narrow icon-only rail and slides
//     open as an overlay on hover, tucking away again on mouse-leave.
//
// The pin choice is the only thing persisted. Hover state is ephemeral.
// `collapsed` = the sidebar is currently a narrow rail (unpinned & not hovered).

import { useCallback, useState } from 'react';

const KEY = 'geni.sidebarPinned';

function readPinned(): boolean {
  try {
    const v = localStorage.getItem(KEY);
    return v === null ? true : v === '1'; // default: pinned/open
  } catch {
    return true;
  }
}

export interface SidebarPref {
  /** True when the sidebar is pinned open. */
  pinned: boolean;
  /** Flip pinned/unpinned and persist. */
  togglePin: () => void;
  /** True when currently a narrow icon rail (unpinned & not hovered). */
  collapsed: boolean;
  /** True when the full-width panel should show (pinned OR hovered). */
  expanded: boolean;
  /** Spread onto the sidebar container to drive hover-to-peek. */
  containerHandlers: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
}

export function useSidebarPref(): SidebarPref {
  const [pinned, setPinned] = useState<boolean>(readPinned);
  const [hovering, setHovering] = useState(false);

  const togglePin = useCallback(() => {
    setPinned((p) => {
      const next = !p;
      try {
        localStorage.setItem(KEY, next ? '1' : '0');
      } catch {
        /* private mode / storage disabled — session-only is fine */
      }
      return next;
    });
  }, []);

  const expanded = pinned || hovering;

  return {
    pinned,
    togglePin,
    collapsed: !expanded,
    expanded,
    containerHandlers: {
      onMouseEnter: () => setHovering(true),
      onMouseLeave: () => setHovering(false),
    },
  };
}
